import { TextSelection } from "prosemirror-state";
import { Fragment, Slice } from "prosemirror-model";
import { CLASSNAME_PREFIX, PARKER_CLASSNAME_PREFIX } from "@/consts/extension";
import { Command } from "prosemirror-commands";
import { EditorView } from "prosemirror-view";

const isOfType = <T>(type: string) => (value: unknown): value is T =>
  typeof value === type;

export const EMPTY_COMMAND = () => () => {};

export const DEFAULT_IS_DISABLED = (): boolean => false;

export const DEFAULT_IS_ACTIVE = (): boolean => false;

export const isString = isOfType("string");

export const isNumber = isOfType("number");

export const isFunction = isOfType("function");

export const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && (isFunction(value) || isOfType("object")(value));

export const isPlainObject = (obj: unknown) => {
  if (typeof obj !== "object" || obj === null) return false;

  let proto = obj;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto;
};

export const inRange = (value: number, range: [number, number]) => {
  if (!isNumber(value) || !Array.isArray(range)) {
    return false;
  }
  if (range.some((p) => !isNumber(p))) {
    return false;
  }
  return value <= Math.max(...range) && value >= Math.min(...range);
};

export const isEqualRangeEdge = (a: Range, b: Range) =>
  a &&
  b &&
  !a.compareBoundaryPoints(Range.START_TO_START, b) &&
  !a.compareBoundaryPoints(Range.END_TO_END, b);

export const isDOMNode = (domNode: unknown): domNode is Node =>
  domNode instanceof Node;

export const deviceSystem = () => {
  const isAndroid = window.navigator.userAgent.match(/android/gi);
  const isIPhone = window.navigator.userAgent.match(/iphone/gi);
  const isIOS = /ipad|iphone/i.test(navigator.userAgent);
  return {
    isAndroid,
    isIPhone,
    isIOS,
  };
};

export const omit = (
  target: Record<string, any> | string[],
  omitKey: string | string[]
) => {
  const filterKey = isString(omitKey) ? [omitKey] : omitKey;
  if (isPlainObject(target)) {
    return Object.keys(target)
      .filter((key) => !filterKey.includes(key))
      .reduce((prev, cur) => {
        prev[cur] = target[cur];
        return prev;
      }, Object.create(null));
  }
  return target.filter((p) => !filterKey.includes(p));
};

export const head = <T>(array: T[]): T => {
  return array[0];
};

export const last = <T>(array: T[]): T => {
  return array[array.length - 1];
};

export const coerr = (err: Error) => {
  (console as any).err(err);
};

export const noop = () => {};

export function getInsertBlockNodes(state, node) {
  const { schema, selection, doc } = state;
  const { paragraph } = schema.nodes;
  const { $from, $to, to } = selection;
  const afterNode = $to.nodeAfter;
  const fromNodeGrandParent = $from.node(-1);
  const fromNodeParent = $from.node();

  // 光标在文档尾部
  if (!afterNode) {
    const nodes = [node, paragraph.create()];

    // 是否在文档尾部后单元格尾部
    const isInTail =
      to === doc.content.size - 1 ||
      (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;

    if (isInTail) return nodes;

    // 后面一行是否为文本块节点（即有内联内容的块级节点，schema中定义content为inline或者text的节点例如段落、标题, ）
    const nextNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;
    if (nextNode && !nextNode.isTextblock && !nextNode.isText) {
      // 后面一行为非文本块节点
      return nodes;
    }
  }

  return [node];
}

const isParagraph = (node) => {
  return node?.type?.name === "paragraph";
};

// 插入块处理
export function insertBlock(
  state,
  node,
  options = {
    selection: null,
    cursorMove: true,
  }
) {
  if (!node.isBlock) {
    throw new Error("必须传入 block node");
  }
  const { tr, schema } = state;
  const selection = options.selection || state.selection;
  const { cursorMove } = options;
  const { table, note } = schema.nodes;
  const { from, $from } = selection;

  // TODO: 为什么需要清除格式
  tr.setStoredMarks([]); // 清除格式
  const insertingNodes = getInsertBlockNodes(state, node);

  // TODO: selection.replace 并不是常用的函数，是否可以替换成 tr.replaceWith
  selection.replace(tr, new Slice(Fragment.from(insertingNodes), 0, 0));
  // transform 1.1 版本会在 replace 过程中增加多余的空行，此处进行判断并清理
  // transform 升级到 1.3 之后可以恢复此段代码的处理，不需要清理多余的空行
  // https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/20632944
  const step = tr.steps[0];
  if (step) {
    const slice = step?.slice;
    const { content } = slice?.content;
    const size = content.length;
    // 末尾两个元素是否为 p 标签
    const removeParagraph =
      size > 2 &&
      isParagraph(content[size - 1]) &&
      isParagraph(content[size - 2]);

    if (removeParagraph) {
      // 如果替换时最后两个node都是 paragraph，说明有多余的空行，需要删除最后一个空行
      const lastPos = tr.mapping.map(step.to);
      tr.delete(lastPos - 2, lastPos);
    }
  }

  tr.setSelection(
    new TextSelection(
      tr.doc.resolve(tr.mapping.map(state.selection.from)),
      tr.doc.resolve(tr.mapping.map(state.selection.to))
    )
  );

  // TODO:下面这段代码优化
  // 移动光标位置
  if (cursorMove) {
    const { nodeBefore } = $from; // 光标前相邻节点
    let offset = nodeBefore ? 2 : 0;
    if (node.type.isLeaf) {
      offset += 1;
    } else if (node.type === note) {
      offset += 4;
    } else if (node.type === table) {
      offset += 3;
    }
    const newPos =
      selection.constructor.name === "GapCursor" ? from : from + offset;
    tr.setSelection(TextSelection.create(tr.doc, newPos));
  }
  return { transaction: tr };
}

export const classNamePrefix = (
  className: string,
  prefix: string = CLASSNAME_PREFIX
): string => `${prefix}-${className}`;

export const classNameParkerPrefix = (className: string): string =>
  classNamePrefix(className, PARKER_CLASSNAME_PREFIX);

/**
 * 格式化文件大小(File.size)，加上单位
 */
export function formatFileSize(origin) {
  if (origin >= 1024 * 1024) {
    return `${(origin / (1024 * 1024)).toFixed(2)}MB`;
  }
  if (origin >= 1024) {
    return `${(origin / 1024).toFixed(2)}KB`;
  }
  return `${origin || 0}B`;
}

/**
 * 键盘上的全部可打印键
 */
export function isPrintableKey(evt) {
  const key = evt.which || evt.keyCode;
  return (
    !(evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey) &&
    (key === 32 || // 空格
      (key >= 48 && key <= 57) || // 数字
      (key >= 65 && key <= 90) || // 字母
      (key >= 96 && key <= 105) || // 小键盘数字
      key === 106 ||
      (key >= 186 && key <= 192) ||
      key >= 219 ||
      key <= 222) // 其他符号键
  );
}
export const getPositiveNumber = (num) => (num < 0 ? 0 : num);

/**
 *格式化时间字符串
 * @param time
 * @returns HH:MM格式字符串
 */
export const formatTime = (time: number): string => {
  const minute = parseInt(`${time / 60}`, 10);
  const second = parseInt(`${time % 60}`, 10);
  return `${minute < 10 ? `0${minute}` : minute}:${
    second < 10 ? `0${second}` : second
  }`;
};

// 获取浏览器和系统信息
export function getSystemInfo() {
  // browser
  const nVer = navigator.appVersion;
  const nAgt = navigator.userAgent;
  let browser = navigator.appName;
  let version = `${parseFloat(navigator.appVersion)}`;
  let nameOffset;
  let verOffset;
  let ix;
  const isMobile = nAgt.toLowerCase().indexOf("mobile") !== -1;

  // Opera
  // eslint-disable-next-line no-cond-assign
  if ((verOffset = nAgt.indexOf("Opera")) !== -1) {
    browser = "Opera";
    version = nAgt.substring(verOffset + 6);
    // eslint-disable-next-line no-cond-assign
    if ((verOffset = nAgt.indexOf("Version")) !== -1) {
      version = nAgt.substring(verOffset + 8);
    }
  }
  // Opera Next
  // eslint-disable-next-line no-cond-assign
  if ((verOffset = nAgt.indexOf("OPR")) !== -1) {
    browser = "Opera";
    version = nAgt.substring(verOffset + 4);

    // Edge
    // eslint-disable-next-line no-cond-assign
  } else if ((verOffset = nAgt.indexOf("Edge")) !== -1) {
    browser = "Microsoft Edge";
    version = nAgt.substring(verOffset + 5);

    // MSIE
    // eslint-disable-next-line no-cond-assign
  } else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
    browser = "Microsoft Internet Explorer";
    version = nAgt.substring(verOffset + 5);

    // Chrome
    // eslint-disable-next-line no-cond-assign
  } else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
    browser = "Chrome";
    version = nAgt.substring(verOffset + 7);

    // Safari
    // eslint-disable-next-line no-cond-assign
  } else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
    browser = "Safari";
    version = nAgt.substring(verOffset + 7);
    // eslint-disable-next-line no-cond-assign
    if ((verOffset = nAgt.indexOf("Version")) !== -1) {
      version = nAgt.substring(verOffset + 8);
    }

    // Firefox
    // eslint-disable-next-line no-cond-assign
  } else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
    browser = "Firefox";
    version = nAgt.substring(verOffset + 8);

    // MSIE 11+
  } else if (nAgt.indexOf("Trident/") !== -1) {
    browser = "Microsoft Internet Explorer";
    version = nAgt.substring(nAgt.indexOf("rv:") + 3);

    // Other browsers
  } else if (
    // eslint-disable-next-line no-cond-assign
    (nameOffset = nAgt.lastIndexOf(" ") + 1) <
    // eslint-disable-next-line no-cond-assign
    (verOffset = nAgt.lastIndexOf("/"))
  ) {
    browser = nAgt.substring(nameOffset, verOffset);
    version = nAgt.substring(verOffset + 1);
    if (browser.toLowerCase() === browser.toUpperCase()) {
      browser = navigator.appName;
    }
  }
  // trim the version string
  // eslint-disable-next-line no-cond-assign
  if ((ix = version.indexOf(";")) !== -1) {
    version = version.substring(0, ix);
  }
  // eslint-disable-next-line no-cond-assign
  if ((ix = version.indexOf(" ")) !== -1) {
    version = version.substring(0, ix);
  }
  // eslint-disable-next-line no-cond-assign
  if ((ix = version.indexOf(")")) !== -1) {
    version = version.substring(0, ix);
  }

  // system
  let os = "unknown";
  const clientStrings = [
    { s: "Windows 10", r: /(Windows 10.0|Windows NT 10.0)/ },
    { s: "Windows 8.1", r: /(Windows 8.1|Windows NT 6.3)/ },
    { s: "Windows 8", r: /(Windows 8|Windows NT 6.2)/ },
    { s: "Windows 7", r: /(Windows 7|Windows NT 6.1)/ },
    { s: "Windows Vista", r: /Windows NT 6.0/ },
    { s: "Windows Server 2003", r: /Windows NT 5.2/ },
    { s: "Windows XP", r: /(Windows NT 5.1|Windows XP)/ },
    { s: "Windows 2000", r: /(Windows NT 5.0|Windows 2000)/ },
    { s: "Windows ME", r: /(Win 9x 4.90|Windows ME)/ },
    { s: "Windows 98", r: /(Windows 98|Win98)/ },
    { s: "Windows 95", r: /(Windows 95|Win95|Windows_95)/ },
    { s: "Windows NT 4.0", r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
    { s: "Windows CE", r: /Windows CE/ },
    { s: "Windows 3.11", r: /Win16/ },
    { s: "Android", r: /Android/ },
    { s: "Open BSD", r: /OpenBSD/ },
    { s: "Sun OS", r: /SunOS/ },
    { s: "Linux", r: /(Linux|X11)/ },
    { s: "iOS", r: /(iPhone|iPad|iPod)/ },
    { s: "Mac OS X", r: /Mac OS X/ },
    { s: "Mac OS", r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
    { s: "QNX", r: /QNX/ },
    { s: "UNIX", r: /UNIX/ },
    { s: "BeOS", r: /BeOS/ },
    { s: "OS/2", r: /OS\/2/ },
    {
      s: "Search Bot",
      r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/,
    },
  ];
  for (const id in clientStrings) {
    const cs = clientStrings[id];
    if (cs.r.test(nAgt)) {
      os = cs.s;
      break;
    }
  }

  let osVersion = "unknown";

  if (/Windows (.*)/.test(os)) {
    osVersion = /Windows (.*)/.exec(os)[1];
    os = "Windows";
  }

  switch (os) {
    case "Mac OS X":
      if (/Mac OS X ([._\d]+)/.test(nAgt)) {
        osVersion = /Mac OS X ([._\d]+)/.exec(nAgt)[1];
      }
      break;

    case "Android":
      if (/Android ([._\d]+)/.test(nAgt)) {
        osVersion = /Android ([._\d]+)/.exec(nAgt)[1];
      }
      break;

    case "iOS":
      if (/OS (\d+)_(\d+)_?(\d+)?/.test(nVer)) {
        const splits = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer);
        // eslint-disable-next-line no-bitwise
        osVersion = `${splits[1]}.${splits[2]}.${+splits[3] | 0}`;
      } else if (/iOS\/(\d+).(\d+).?(\d+)?/.test(nVer)) {
        const splits = /iOS\/(\d+).(\d+).?(\d+)?/.exec(nVer);
        // eslint-disable-next-line no-bitwise
        osVersion = `${splits[1]}.${splits[2]}.${+splits[3] | 0}`;
      }
      break;

    default:
      break;
  }

  return {
    browser: browser || "unknown",
    browserVersion: version || "unknown",
    os: os || "unknown",
    osVersion: osVersion || "unknown",
    isMobile,
    isWindows: ~(os || "unknown").toLowerCase().indexOf("win"),
  };
}

export const domAtPos = (view: EditorView, pos, side?) => {
  const res = view.domAtPos(pos, side);
  if (res === null) {
    return null;
  }
  const { node } = res;
  let { offset } = res;
  while (node && node.childNodes[offset]) {
    const dom = node.childNodes[offset] as HTMLElement;
    if (dom.classList?.contains(`ProseMirror-widget`)) {
      offset++;
      continue;
    }
    return dom;
  }
  return null;
};

export const execAfterCommands = (
  commands: (...cmdArgs: any[]) => Command,
  someThingFn: (result: boolean) => void = noop
) => (...cmdArgs) => (state, dispatch, view?) => {
  const _ = commands(...cmdArgs)(state, dispatch, view);
  someThingFn(_);
  return _;
};

export const formatDateTime = (time, showFullYear = false) => {
  const nowTime = Date.now();
  const dateObj = new Date(time);
  const nowObj = new Date(nowTime);
  const todayTime = new Date(
    nowObj.getFullYear(),
    nowObj.getMonth(),
    nowObj.getDate()
  ).getTime();

  const month = dateObj.getMonth() + 1;
  const displayMonth = month < 10 ? `0${month}` : month;
  const date = dateObj.getDate();
  const displayDate = date < 10 ? `0${date}` : date;
  const hour = dateObj.getHours();
  const displayHour = hour < 10 ? `0${hour}` : hour;
  const minute = dateObj.getMinutes();
  const displayMinute = minute < 10 ? `0${minute}` : minute;

  if (showFullYear) {
    return `${dateObj.getFullYear()}-${displayMonth}-${displayDate} ${displayHour}:${displayMinute}`;
  }
  if (nowObj.getFullYear() !== dateObj.getFullYear()) {
    return `${dateObj.getFullYear()}-${displayMonth}-${displayDate} ${displayHour}:${displayMinute}`;
  }

  if (time >= todayTime) {
    return `今天 ${displayHour}:${displayMinute}`;
  }
  if (todayTime - time < 864e5) {
    return `昨天 ${displayHour}:${displayMinute}`;
  }
  return `${displayMonth}-${displayDate} ${displayHour}:${displayMinute}`;
};

export const makeTask = () => {
  let _isDead = false;
  return {
    exec: (cb) => !_isDead && cb(),
    close: () => {
      _isDead = true;
    },
    check: () => {
      return !_isDead;
    },
  };
};
